"use client";

import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import { useStore } from "@/lib/store";
import { AgentRunCard } from "./AgentRunCard";

/**
 * Shared list of agent runs. Used inline in IssueDetail (scope=issue),
 * inside DocsView (scope=doc), and in the workspace-wide AgentRunsView
 * (scope=workspace).
 */
type Scope =
  | { kind: "issue"; id: string }
  | { kind: "doc"; id: string }
  | { kind: "workspace" };

export function AgentRunsSection({
  scope,
  limit = 50,
  emptyHidden = false,
}: {
  scope: Scope;
  limit?: number;
  emptyHidden?: boolean;
}) {
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const allRuns = useStore((s) => s.agentRuns);

  const runs = useMemo(() => {
    return allRuns
      .filter((r) => {
        if (r.workspaceId !== activeWorkspaceId) return false;
        if (scope.kind === "issue") return r.touchedIssueIds.includes(scope.id);
        if (scope.kind === "doc") return r.touchedDocIds.includes(scope.id);
        return true; // workspace
      })
      .slice(0, limit);
  }, [allRuns, activeWorkspaceId, scope, limit]);

  // Default: expanded if 0–2 runs (legible), collapsed otherwise (so a
  // 6-run debug session doesn't bury the issue's metadata sidebar).
  // Workspace scope stays always-expanded — that view IS the runs feed.
  // ⚠️ This hook MUST run before the empty-state returns below: on a cold
  // ?issue= deep link the first render has 0 runs (store still loading) and
  // returned early — once runs arrived, the extra useState blew up with
  // React #310 ("rendered more hooks than during the previous render").
  const collapsible = scope.kind !== "workspace";
  const [open, setOpen] = useState(!collapsible || runs.length <= 2);

  if (runs.length === 0) {
    if (emptyHidden) return null;
    return (
      <div className="text-[11.5px] text-[var(--fg-muted)] italic px-1 py-3">
        No agent runs yet for this {scope.kind}. Agents record their work via{" "}
        <code className="text-[10.5px]">record_run</code> after substantive tasks.
      </div>
    );
  }

  if (!collapsible) {
    return (
      <div className="space-y-2">
        {runs.map((r) => (
          <AgentRunCard key={r.id} run={r} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-[12px] font-medium text-[var(--fg-secondary)] hover:text-[var(--fg)] transition-colors w-full"
      >
        {open ? (
          <ChevronDown size={11} className="text-[var(--fg-tertiary)]" />
        ) : (
          <ChevronRight size={11} className="text-[var(--fg-tertiary)]" />
        )}
        <Sparkles size={12} className="text-[var(--accent)]" />
        Agent runs
        <span className="text-[var(--fg-muted)]">{runs.length}</span>
      </button>
      {open &&
        runs.map((r) => <AgentRunCard key={r.id} run={r} />)}
    </div>
  );
}
